import React, { useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { mockData } from '../mock';
import { ChevronDown } from 'lucide-react';

export const Hero = () => {
  const containerRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ['start start', 'end start']
  });

  const y = useTransform(scrollYProgress, [0, 1], ['0%', '50%']);
  const opacity = useTransform(scrollYProgress, [0, 0.5], [1, 0]);

  return (
    <section
      id="hero"
      ref={containerRef}
      className="relative min-h-screen flex items-center justify-center overflow-hidden"
    >
      {/* Background Image with Parallax */}
      <motion.div
        style={{ y }}
        className="absolute inset-0 z-0"
      >
        <div className="absolute inset-0 bg-gradient-to-b from-[#0a1628]/90 via-[#0a1628]/70 to-[#0a1628] z-10" />
        <img
          src="https://images.unsplash.com/photo-1590430752967-d0e116909be1"
          alt="Premium Ice"
          className="w-full h-full object-cover"
        />
      </motion.div>

      {/* Animated Grain Texture */}
      <div className="absolute inset-0 z-10 opacity-[0.03] mix-blend-overlay pointer-events-none"
        style={{
          backgroundImage: 'url("data:image/svg+xml,%3Csvg viewBox=\'0 0 200 200\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cfilter id=\'noiseFilter\'%3E%3CfeTurbulence type=\'fractalNoise\' baseFrequency=\'0.9\' numOctaves=\'4\' stitchTiles=\'stitch\'/%3E%3C/filter%3E%3Crect width=\'100%25\' height=\'100%25\' filter=\'url(%23noiseFilter)\'/%3E%3C/svg%3E")',
          backgroundRepeat: 'repeat'
        }}
      />

      {/* Content */}
      <motion.div
        style={{ opacity }}
        className="relative z-20 text-center px-6 max-w-6xl mx-auto"
      >
        {/* Logo Reveal */}
        <div className="overflow-hidden mb-8">
          <motion.div
            initial={{ y: 100, opacity: 0, scale: 0.8 }}
            animate={{ y: 0, opacity: 1, scale: 1 }}
            transition={{ duration: 1.2, ease: [0.25, 0.1, 0.25, 1] }}
            className="flex justify-center relative"
          >
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[380px] h-[380px] md:w-[520px] md:h-[520px] bg-white/25 rounded-full blur-3xl pointer-events-none" />
            <img 
              src="https://customer-assets-4nw71qhi.emergentagent.net/job_flavored-ice-factory/artifacts/iyqyn1tc_IMG_3682.png"
              alt="Império Ice"
              className="h-48 md:h-64 w-auto object-contain drop-shadow-[0_0_40px_rgba(255,255,255,0.5)] relative z-10"
            />
          </motion.div>
        </div>

        <div className="overflow-hidden mb-8">
          <motion.p
            initial={{ y: 50, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="text-xl md:text-2xl text-white/70 font-light tracking-wide"
          >
            {mockData.hero.tagline}
          </motion.p>
        </div>

        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.6, delay: 0.6 }}
          className="mt-12"
        >
          <motion.button
            onClick={() => document.querySelector('#products')?.scrollIntoView({ behavior: 'smooth' })}
            className="group relative px-12 py-5 bg-gradient-to-r from-[#7dd3fc] to-[#bae6fd] text-[#0a1628] font-bold text-lg tracking-wide rounded-full overflow-hidden shadow-xl shadow-[#7dd3fc]/30"
            whileHover={{ scale: 1.05, boxShadow: '0 20px 40px rgba(125, 211, 252, 0.4)' }}
            whileTap={{ scale: 0.95 }}
          >
            <span className="relative z-10">Conheça Nossos Produtos</span>
            <motion.div
              className="absolute inset-0 bg-white"
              initial={{ x: '-100%' }}
              whileHover={{ x: 0 }}
              transition={{ duration: 0.3 }}
            />
          </motion.button>
        </motion.div>
      </motion.div>

      {/* Scroll Indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1, duration: 1 }}
        className="absolute bottom-10 left-1/2 transform -translate-x-1/2 z-20"
      >
        <motion.div
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 1.5, repeat: Infinity, ease: 'easeInOut' }}
        >
          <ChevronDown size={32} className="text-white/50" />
        </motion.div>
      </motion.div>

      {/* Decorative Elements */}
      <div className="absolute top-1/4 left-10 w-2 h-2 bg-[#7dd3fc] rounded-full opacity-50 animate-pulse" />
      <div className="absolute top-1/3 right-20 w-3 h-3 bg-[#7dd3fc] rounded-full opacity-30 animate-pulse" style={{ animationDelay: '0.5s' }} />
      <div className="absolute bottom-1/4 left-1/4 w-2 h-2 bg-[#7dd3fc] rounded-full opacity-40 animate-pulse" style={{ animationDelay: '1s' }} />
    </section>
  );
};
